import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { SignupShell } from '@/features/auth/components/SignupShell';
import { useForgotPasswordMutation } from '@/features/auth/api/useAuthMutations';

type ForgotForm = {
  identifier: string;
};

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const forgotPassword = useForgotPasswordMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotForm>({ mode: 'onSubmit' });

  const onSubmit = (data: ForgotForm) => {
    forgotPassword.mutate({ identifier: data.identifier.trim() });
  };

  return (
    <SignupShell>
      <div className="signup-form-align flex w-full min-w-0 flex-col">
        <h1 className="mb-2 text-[22px] font-semibold tracking-tight text-[#0F1817]">
          Forgot password?
        </h1>
        <p className="mb-6 text-sm leading-relaxed text-[#7C8B88]">
          No worries! Enter your email address below and we&apos;ll send you an
          OTP to reset your password.
        </p>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex w-full min-w-0 flex-col gap-1 text-left"
        >
          <div className="mb-4">
            <label className="mb-1.5 block text-[12.5px] font-medium text-[#3E524F]">
              Email address
            </label>
            <input
              type="email"
              autoComplete="email"
              placeholder="Enter your email"
              className={`signup-input box-border h-[44px] w-full min-w-0 rounded-[9px] border bg-white px-3.5 text-sm leading-none text-[#0F1817] outline-none transition-[border-color,box-shadow] placeholder:text-[#A9B4B1] ${
                errors.identifier
                  ? 'border-red-400'
                  : 'border-[#E2E9E7] hover:border-[#C9D3D1]'
              }`}
              {...register('identifier', {
                required: 'Email is required',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Enter a valid email address',
                },
              })}
            />
            {errors.identifier ? (
              <p className="mt-1 text-[11px] text-red-500">
                {errors.identifier.message}
              </p>
            ) : null}
          </div>

          <button
            type="submit"
            disabled={forgotPassword.isPending}
            className="signup-btn flex h-[46px] w-full items-center justify-center rounded-[9px] border-none text-[14.5px] font-semibold text-white transition-all hover:enabled:-translate-y-px disabled:cursor-not-allowed disabled:opacity-45"
          >
            {forgotPassword.isPending ? 'Sending…' : 'Send OTP'}
          </button>
        </form>

        <button
          type="button"
          onClick={() => navigate('/login')}
          className="mt-5 border-none bg-transparent p-0 text-sm text-[#7C8B88] transition-colors hover:text-[#0F1817]"
        >
          ← Back to login
        </button>
      </div>
    </SignupShell>
  );
}
